import { Modal, Form, Input, InputNumber, Select } from "antd"
import { FC, useEffect } from "react"
import { SubmitCancelButtons } from "components"
import {
  createGameChallenge,
  updateGameChallenge,
  CreateGameChallengeRequest,
  UpdateGameChallengeRequest,
  GameChallenge,
} from "../../../api"
import { notification } from "antd"

const { Option } = Select

interface ChallengeModalProps {
  visible: boolean;
  onClose: () => void;
  editType: "create" | "edit";
  contestPeriodId: string;
  gameId: number;
  editingChallenge?: GameChallenge | null;
  loading: boolean;
  onSuccess: () => void;
}

export const ChallengeModal: FC<ChallengeModalProps> = ({
  visible,
  onClose,
  editType,
  contestPeriodId,
  gameId,
  editingChallenge,
  loading,
  onSuccess,
}) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (editType === "edit" && editingChallenge && visible) {
      form.setFieldsValue({
        title: editingChallenge.title,
        actionText: editingChallenge.actionText,
        type: editingChallenge.type,
        identifier: editingChallenge.identifier,
        targetValue: editingChallenge.targetValue,
        goal: editingChallenge.goal,
      })
    }
  }, [editType, editingChallenge, visible, form])

  const handleSubmit = async(values: {
    title: string;
    actionText: string;
    type: "ACCUMULATIVE" | "NOT_ACCUMULATIVE";
    identifier: string;
    targetValue: number;
    goal: number;
  }) => {
    try {
      if (editType === "create") {
        const createData: CreateGameChallengeRequest = {
          title: values.title,
          actionText: values.actionText,
          type: values.type,
          identifier: values.identifier,
          targetValue: values.targetValue,
          goal: values.goal,
        }
        await createGameChallenge(contestPeriodId, gameId, createData)
        notification.success({ message: "Challenge created successfully" })
      } else {
        if (!editingChallenge) return
        const updateData: UpdateGameChallengeRequest = {
          title: values.title,
          actionText: values.actionText,
          type: values.type,
          identifier: values.identifier,
          targetValue: values.targetValue,
          goal: values.goal,
        }
        await updateGameChallenge(editingChallenge.id, updateData)
        notification.success({ message: "Challenge updated successfully" })
      }

      onClose()
      form.resetFields()
      onSuccess()
    } catch (error) {
      console.error(`Failed to ${editType} challenge:`, error)
      notification.error({ message: `Failed to ${editType} challenge` })
    }
  }

  const handleClose = () => {
    onClose()
    form.resetFields()
  }

  return (
    <Modal
      visible={visible}
      title={editType === "create" ? "Create new challenge" : "Edit challenge"}
      width="600px"
      footer={null}
      onCancel={handleClose}
      zIndex={1200}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please enter challenge title" }]}
        >
          <Input
            placeholder="Enter challenge title"
          />
        </Form.Item>

        <Form.Item
          label="Action Text"
          name="actionText"
        >
          <Input
            placeholder="Enter action text"
          />
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Please select challenge type" }]}
        >
          <Select
            placeholder="Select challenge type"
            getPopupContainer={trigger => trigger.parentElement}
            style={{ width: "100%" }}
          >
            <Option value="ACCUMULATIVE">ACCUMULATIVE</Option>
            <Option value="NOT_ACCUMULATIVE">NOT_ACCUMULATIVE</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Identifier"
          name="identifier"
          rules={[{ required: true, message: "Please enter identifier" }]}
        >
          <Input
            placeholder="Enter identifier"
          />
        </Form.Item>

        <Form.Item
          label="Target Value"
          name="targetValue"
          dependencies={["type"]}
          rules={[
            ({ getFieldValue }) => ({
              required: getFieldValue("type") !== "ACCUMULATIVE",
              message: "Please enter target value",
            }),
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter target value"
            min={0}
          />
        </Form.Item>

        <Form.Item
          label="Goal"
          name="goal"
          rules={[{ required: true, message: "Please enter goal" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter goal"
            min={0}
          />
        </Form.Item>

        <SubmitCancelButtons
          onClose={handleClose}
          isLoading={loading}
        />
      </Form>
    </Modal>
  )
}
